import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PageShell } from "../../components/layout/PageShell";

type MdProps = { children?: React.ReactNode };

const mdComponents: React.ComponentProps<typeof ReactMarkdown>["components"] = {
  h1: ({ children }: MdProps) => (
    <h1 className="text-2xl font-semibold text-slate-900 mb-4 mt-0">{children}</h1>
  ),
  h2: ({ children }: MdProps) => (
    <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-3 pb-2 border-b border-slate-200">
      {children}
    </h2>
  ),
  h3: ({ children }: MdProps) => (
    <h3 className="text-base font-semibold text-slate-800 mt-6 mb-2">{children}</h3>
  ),
  h4: ({ children }: MdProps) => (
    <h4 className="text-sm font-semibold text-slate-700 mt-4 mb-1">{children}</h4>
  ),
  p: ({ children }: MdProps) => (
    <p className="text-sm text-slate-700 leading-relaxed mb-3">{children}</p>
  ),
  ul: ({ children }: MdProps) => (
    <ul className="list-disc list-inside space-y-1 mb-3 text-sm text-slate-700 pl-2">
      {children}
    </ul>
  ),
  ol: ({ children }: MdProps) => (
    <ol className="list-decimal list-inside space-y-1 mb-3 text-sm text-slate-700 pl-2">
      {children}
    </ol>
  ),
  li: ({ children }: MdProps) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }: MdProps) => (
    <strong className="font-semibold text-slate-900">{children}</strong>
  ),
  em: ({ children }: MdProps) => <em className="italic text-slate-700">{children}</em>,
  code: ({ children }: MdProps) => (
    <code className="bg-slate-100 text-slate-800 rounded px-1 py-0.5 text-xs font-mono">
      {children}
    </code>
  ),
  blockquote: ({ children }: MdProps) => (
    <blockquote className="border-l-4 border-blue-300 pl-4 my-3 text-sm text-slate-600 italic bg-blue-50/50 py-2 rounded-r">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-6 border-slate-200" />,
  table: ({ children }: MdProps) => (
    <div className="overflow-x-auto mb-4">
      <table className="min-w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }: MdProps) => <thead className="bg-slate-100">{children}</thead>,
  tbody: ({ children }: MdProps) => (
    <tbody className="divide-y divide-slate-100">{children}</tbody>
  ),
  tr: ({ children }: MdProps) => <tr>{children}</tr>,
  th: ({ children }: MdProps) => (
    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wide">
      {children}
    </th>
  ),
  td: ({ children }: MdProps) => (
    <td className="px-4 py-2 text-slate-700 align-top">{children}</td>
  ),
};

type HelpPageProps = {
  src?: string;
  title?: string;
};

export default function HelpPage({ src = "/manuale.md", title = "Manuale operativo" }: HelpPageProps) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setContent(null);
    setError(false);
    fetch(src)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.text();
      })
      .then(setContent)
      .catch(() => setError(true));
  }, [src]);

  return (
    <PageShell title={title} subtitle="">
      <div className="max-w-3xl">
        {error && (
          <p className="text-sm text-red-600">Impossibile caricare il manuale.</p>
        )}
        {!error && !content && (
          <p className="text-sm text-gray-400">Caricamento…</p>
        )}
        {content && (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
            {content}
          </ReactMarkdown>
        )}
      </div>
    </PageShell>
  );
}
